import { useCallback, useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Button, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTheme } from '@components/theme-v2/ThemeContext';

// Pricing constant (should match backend)
const ADDITIONAL_CONTACTS_PRICE = 0.25;

// Structured contact object
interface AdditionalContact {
  name?: string;
  email?: string;
  phone?: string;
  title?: string;
}

interface AdditionalContactsProps {
  leadId: string;
  // Structured contacts (preferred)
  additionalContacts?: AdditionalContact[];
  // Legacy flat arrays (fallback)
  additionalEmails?: string[];
  additionalPhones?: string[];
  isUnlocked: boolean;
  onUnlock: (leadId: string) => Promise<{
    success: boolean;
    additional_contacts?: AdditionalContact[];
    additional_emails?: string[];
    additional_phones?: string[];
    error?: string;
  }>;
  userCredits?: number;
  onCreditsDeducted?: (amount: number, newBalance: number) => void;
}

export const AdditionalContacts: React.FC<AdditionalContactsProps> = ({
  leadId,
  additionalContacts = [],
  additionalEmails = [],
  additionalPhones = [],
  isUnlocked,
  onUnlock,
  userCredits = 0,
  onCreditsDeducted,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(isUnlocked);
  const [revealedContacts, setRevealedContacts] = useState<AdditionalContact[]>(isUnlocked ? additionalContacts : []);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // Sync unlocked state with props (for when data is refreshed from server)
  useEffect(() => {
    if (isUnlocked && !unlocked) {
      setUnlocked(true);
      setRevealedContacts(additionalContacts);
    }
  }, [isUnlocked, additionalContacts, unlocked]);


  // Build contacts from structured data or fallback to flat arrays
  const buildContactsList = useCallback((): AdditionalContact[] => {
    if (additionalContacts && additionalContacts.length > 0) {
      return additionalContacts;
    }
    // Fallback: build from flat arrays
    const maxCount = Math.max(additionalEmails.length, additionalPhones.length);
    const contacts: AdditionalContact[] = [];
    for (let i = 0; i < maxCount; i++) {
      const contact: AdditionalContact = {
        name: '',
        email: additionalEmails[i] || '',
        phone: additionalPhones[i] || '',
      };
      if (contact.email || contact.phone) {
        contacts.push(contact);
      }
    }
    return contacts;
  }, [additionalContacts, additionalEmails, additionalPhones]);

  const contactsList = buildContactsList();
  const totalAdditionalCount = contactsList.length;

  // Calculate total cost for unlocking all contacts
  const totalCost = totalAdditionalCount * ADDITIONAL_CONTACTS_PRICE;
  const formattedTotalCost = totalCost % 1 === 0 ? totalCost.toString() : totalCost.toFixed(2);

  // Theme-based colors
  const bgColor = isDark ? 'rgba(0, 212, 170, 0.08)' : 'rgba(0, 212, 170, 0.05)';
  const borderColor = isDark ? 'rgba(0, 212, 170, 0.3)' : 'rgba(0, 212, 170, 0.2)';
  const blurOverlayBg = isDark ? 'rgba(26, 31, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)';
  const textColor = isDark ? '#ffffff' : '#1a1f2e';
  const mutedColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const contactBgColor = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';

  const handleUnlock = useCallback(async () => {
    if (unlocked || loading) {
      return;
    }

    // Check if user has enough credits for all contacts
    if (userCredits < totalCost) {
      // eslint-disable-next-line no-alert
      window.alert(
        `Insufficient credits. You need ${formattedTotalCost} credits to unlock ${totalAdditionalCount} contact${totalAdditionalCount > 1 ? 's' : ''}. Current balance: ${userCredits}`,
      );
      return;
    }

    setLoading(true);
    try {
      const result = await onUnlock(leadId);

      if (result.success) {
        // Build contacts from all available data sources
        const contacts: AdditionalContact[] = [];
        const rawContacts = result.additional_contacts || [];
        const emails = result.additional_emails || additionalEmails || [];
        const phones = result.additional_phones || additionalPhones || [];

        // Check if additional_contacts contains structured objects or just strings
        const hasStructuredContacts =
          rawContacts.length > 0 && typeof rawContacts[0] === 'object' && rawContacts[0] !== null;

        if (hasStructuredContacts) {
          // Check if contacts are in {type, value} format (e.g., {type: "phone", value: "+1..."})
          const firstContact = rawContacts[0] as Record<string, unknown>;
          const isTypeValueFormat = 'type' in firstContact && 'value' in firstContact;

          if (isTypeValueFormat) {
            // Group by type and build contacts from {type, value} pairs
            const phoneValues: string[] = [];
            const emailValues: string[] = [];
            const nameValues: string[] = [];

            rawContacts.forEach((c: unknown) => {
              const item = c as { type: string; value: string };
              if (item.type === 'phone' && item.value) {
                phoneValues.push(item.value);
              } else if (item.type === 'email' && item.value) {
                emailValues.push(item.value);
              } else if (item.type === 'name' && item.value) {
                nameValues.push(item.value);
              }
            });

            // Build contacts pairing available data
            const maxCount = Math.max(phoneValues.length, emailValues.length, nameValues.length);
            for (let i = 0; i < maxCount; i++) {
              contacts.push({
                name: nameValues[i] || '',
                email: emailValues[i] || '',
                phone: phoneValues[i] || '',
                title: '',
              });
            }
          } else {
            // Normalize structured contact objects with standard field names
            rawContacts.forEach((c: unknown) => {
              const contact = c as Record<string, unknown>;
              contacts.push({
                name: String(contact.name || contact.Name || contact.full_name || contact.fullName || ''),
                email: String(contact.email || contact.Email || contact.mail || contact.Mail || ''),
                phone: String(
                  contact.phone ||
                    contact.Phone ||
                    contact.telephone ||
                    contact.Telephone ||
                    contact.mobile ||
                    contact.Mobile ||
                    '',
                ),
                title: String(
                  contact.title ||
                    contact.Title ||
                    contact.position ||
                    contact.Position ||
                    contact.job_title ||
                    contact.jobTitle ||
                    '',
                ),
              });
            });
          }
        } else {
          // Build contacts from separate arrays (phones might be in additional_contacts as strings)
          const allPhones =
            rawContacts.length > 0 && typeof rawContacts[0] === 'string'
              ? [...(rawContacts as string[]), ...phones]
              : phones;

          // Deduplicate phones
          const uniquePhones = [...new Set(allPhones)];

          const maxCount = Math.max(emails.length, uniquePhones.length);
          for (let i = 0; i < maxCount; i++) {
            contacts.push({
              name: '',
              email: emails[i] || '',
              phone: uniquePhones[i] || '',
            });
          }
        }

        // Use functional updates to ensure state is set correctly
        setUnlocked(true);
        setRevealedContacts(
          contacts.length > 0 ? contacts : [{ name: '', email: '', phone: 'Contact data available' }],
        );

        // Notify parent about credit deduction (total cost for all contacts)
        if (onCreditsDeducted) {
          onCreditsDeducted(totalCost, userCredits - totalCost);
        }
      } else {
        // eslint-disable-next-line no-alert
        window.alert(`Unlock failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      // eslint-disable-next-line no-alert
      window.alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [
    leadId,
    unlocked,
    loading,
    userCredits,
    totalCost,
    formattedTotalCost,
    totalAdditionalCount,
    onUnlock,
    onCreditsDeducted,
    additionalEmails,
    additionalPhones,
  ]);

  const handleCopy = useCallback((value: string, type: string) => {
    navigator.clipboard.writeText(value);
    setCopyFeedback(`${type} copied!`);
    setTimeout(() => setCopyFeedback(null), 2000);
  }, []);

  // Blur/mask a value for display when locked
  const maskValue = (value: string): string => {
    if (!value || value.length <= 4) return '****';
    return value.substring(0, 2) + '*'.repeat(value.length - 4) + value.substring(value.length - 2);
  };

  // If no additional contacts, don't render anything
  if (totalAdditionalCount === 0) {
    return null;
  }

  // When unlocked, prefer revealedContacts, fallback to contactsList if empty
  const displayContacts = unlocked ? (revealedContacts.length > 0 ? revealedContacts : contactsList) : contactsList;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: '12px',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {unlocked ? (
            <LockOpenIcon sx={{ color: '#00d4aa', fontSize: 20 }} />
          ) : (
            <LockIcon sx={{ color: '#ffb74d', fontSize: 20 }} />
          )}
          <Typography
            variant="subtitle2"
            sx={{
              color: textColor,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            Additional Contacts
          </Typography>
          <Chip
            label={
              unlocked
                ? `${displayContacts.length} unlocked`
                : `${totalAdditionalCount} contact${totalAdditionalCount > 1 ? 's' : ''}`
            }
            size="small"
            sx={{
              backgroundColor: unlocked ? 'rgba(0, 212, 170, 0.2)' : 'rgba(255, 183, 77, 0.2)',
              color: unlocked ? '#00d4aa' : '#ffb74d',
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        </Box>

        {!unlocked && (
          <Button
            variant="contained"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleUnlock();
            }}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon sx={{ fontSize: 14 }} />
            }
            title={`Unlock ${totalAdditionalCount} contact${totalAdditionalCount > 1 ? 's' : ''} for ${formattedTotalCost} credits`}
            sx={{
              backgroundColor: '#00d4aa',
              color: '#1a1f2e',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              minHeight: 'auto',
              '&:hover': {
                backgroundColor: '#00e5b8',
              },
              '&:disabled': {
                opacity: 0.7,
                backgroundColor: '#00d4aa',
                color: '#1a1f2e',
              },
            }}
          >
            Unlock for {formattedTotalCost} credits
          </Button>
        )}
      </Box>

      {/* Copy feedback */}
      {copyFeedback && (
        <Typography
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: '0.7rem',
            color: '#00d4aa',
            backgroundColor: isDark ? 'rgba(0, 212, 170, 0.2)' : 'rgba(0, 212, 170, 0.1)',
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            zIndex: 10,
          }}
        >
          {copyFeedback}
        </Typography>
      )}

      {/* Content - blurred or revealed */}
      <Box sx={{ position: 'relative' }}>
        {/* Contact Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {displayContacts.map((contact, idx) => (
            <Box
              key={`contact-${contact.email || contact.phone || idx}`}
              sx={{
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: contactBgColor,
                filter: unlocked ? 'none' : 'blur(4px)',
                userSelect: unlocked ? 'auto' : 'none',
              }}
            >
              {/* Name row - always show for unlocked contacts to indicate contact number */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PersonIcon sx={{ color: '#00d4aa', fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: textColor,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    flex: 1,
                  }}
                >
                  {unlocked ? contact.name || `Contact ${idx + 1}` : maskValue(contact.name || 'John Doe')}
                </Typography>
                {unlocked && contact.name && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(contact.name!, 'Name')}
                    sx={{ color: mutedColor, '&:hover': { color: '#00d4aa' }, p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Box>

              {/* Title row */}
              {contact.title && unlocked && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 3 }}>
                  <WorkIcon sx={{ color: '#81c784', fontSize: 14 }} />
                  <Typography variant="caption" sx={{ color: mutedColor, fontSize: '0.75rem' }}>
                    {contact.title}
                  </Typography>
                </Box>
              )}

              {/* Email row - always show when unlocked to indicate availability */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EmailIcon sx={{ color: '#ba68c8', fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: contact.email ? textColor : mutedColor,
                    fontSize: '0.8rem',
                    flex: 1,
                    fontStyle: contact.email ? 'normal' : 'italic',
                  }}
                >
                  {unlocked ? contact.email || 'No email available' : maskValue(contact.email || 'email@example.com')}
                </Typography>
                {unlocked && contact.email && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(contact.email!, 'Email')}
                    sx={{ color: mutedColor, '&:hover': { color: '#00d4aa' }, p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Box>

              {/* Phone row - always show when unlocked to indicate availability */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ color: '#4fc3f7', fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: contact.phone ? textColor : mutedColor,
                    fontSize: '0.8rem',
                    flex: 1,
                    fontStyle: contact.phone ? 'normal' : 'italic',
                  }}
                >
                  {unlocked ? contact.phone || 'No phone available' : maskValue(contact.phone || '555-123-4567')}
                </Typography>
                {unlocked && contact.phone && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(contact.phone!, 'Phone')}
                    sx={{ color: mutedColor, '&:hover': { color: '#00d4aa' }, p: 0.25 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Blur overlay when locked */}
        {!unlocked && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: blurOverlayBg,
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: mutedColor,
                textAlign: 'center',
                fontSize: '0.8rem',
              }}
            >
              Click &quot;Unlock&quot; to reveal {totalAdditionalCount} additional contact
              {totalAdditionalCount > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdditionalContacts;
